import { useMutation, useQuery } from '@tanstack/react-query'
import { toast } from 'sonner'
import { queryClient, trpc } from '@/lib/trpc'
import { Button } from '@deepguide-ai/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@deepguide-ai/ui/dialog'
import type { ConfigModalProps, IntegrationProvider } from '../types'
import { IntegrationIcon } from '../icons'
import { openOAuthConnectPopup } from '../oauth-connect-lifecycle'

const cleanupWarning = 'Previous Aikido credential cleanup is pending. Reauthorize or disconnect to retry cleanup.'

function AikidoConfigModal({ open, onOpenChange, onComplete, workspaceId }: ConfigModalProps) {
  const status = useQuery({ ...trpc.integrations.aikido.status.queryOptions(), enabled: open, refetchInterval: 5000 })
  const integrationId = status.data?.integrationId
  const enabled = useMutation(trpc.integrations.aikido.setEnabled.mutationOptions())
  const disconnect = useMutation(trpc.integrations.deleteIntegration.mutationOptions())
  const connect = useMutation({ mutationFn: () => AikidoProvider.handleConnect({ workspaceId, onSuccess: () => {
    void queryClient.invalidateQueries({ queryKey: trpc.integrations.aikido.status.queryKey() })
    onComplete()
  } }) })
  return <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent>
      <DialogHeader>
        <DialogTitle>Aikido Security</DialogTitle>
        <DialogDescription>Authorize read-only access to Aikido Security. Third-party OAuth currently supports EU workspaces only.</DialogDescription>
      </DialogHeader>
      <div className='space-y-2 px-6 text-sm'>
        {status.isError && <p role='alert' className='text-destructive'>Could not load the connection.</p>}
        {status.data && !status.data.oauthConfigured && <p role='alert'>Ask your administrator to configure the Aikido OAuth application.</p>}
        {status.data?.requiresReauthorization && <p>Reauthorize this existing connection to use OAuth. Existing risks will be preserved.</p>}
        {status.data?.connectionId && <>
          <p>Source workspace: {status.data.sourceWorkspaceId}</p>
          <p>{status.data.enabled ? 'Enabled' : 'Disabled'} · {status.data.status}</p>
          <p className='text-muted-foreground'>Last successful sync: {status.data.lastSyncAt?.toLocaleString() ?? 'Not synced yet'}</p>
        </>}
        {status.data?.disconnectPending
          ? <p role='alert' className='text-status-orange'>Disconnect cleanup is pending. Retry Disconnect before connecting again.</p>
          : status.data?.cleanupPending && <p role='alert' className='text-status-orange'>{cleanupWarning}</p>}
        {status.data?.lastError && <p role='alert' className='break-words text-destructive'>{status.data.lastError}</p>}
      </div>
      <DialogFooter className='flex-wrap'>
        <Button variant='ghost' onClick={() => onOpenChange(false)}>Close</Button>
        {status.data?.connectionId && !status.data.disconnectPending && <Button variant='outline' disabled={enabled.isPending} onClick={() => enabled.mutate({ enabled: !status.data?.enabled }, {
          onSuccess: () => { void queryClient.invalidateQueries({ queryKey: trpc.integrations.aikido.status.queryKey() }); onComplete() },
          onError: error => toast.error(error.message),
        })}>{status.data.enabled ? 'Disable' : 'Enable'}</Button>}
        {integrationId && <Button variant='destructive' disabled={disconnect.isPending} onClick={() => disconnect.mutate({ id: integrationId }, {
          onSuccess: () => { onOpenChange(false); onComplete() }, onError: error => toast.error(error.message),
          onSettled: () => queryClient.invalidateQueries({ queryKey: trpc.integrations.aikido.status.queryKey() }),
        })}>Disconnect</Button>}
        <Button disabled={!status.data?.oauthConfigured || status.data.disconnectPending || connect.isPending} onClick={() => connect.mutate()}>{integrationId ? 'Reauthorize' : 'Connect'}</Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
}

export const AikidoProvider: IntegrationProvider = {
  id: 'Aikido', name: 'Aikido Security', description: 'Connect Aikido Security',
  Icon: () => <IntegrationIcon type='Aikido' />, ConfigModal: AikidoConfigModal,
  handleConnect: async ({ onSuccess, onFinish }) => {
    try {
      const result = await queryClient.getMutationCache().build(queryClient, trpc.integrations.aikido.initAuth.mutationOptions()).execute(undefined)
      openOAuthConnectPopup({ authUrl: result.authUrl, provider: 'Aikido', windowName: 'aikido-auth', onSuccess: result => {
        if (result && typeof result === 'object') {
          if ('cleanupPending' in result && result.cleanupPending === true) toast.warning(cleanupWarning)
        }
        onSuccess?.(result)
      }, onFinish,
        onError: message => toast.error(message), onPopupBlocked: () => toast.error('Allow pop-ups to authorize Aikido.') })
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Could not start Aikido authorization')
      onFinish?.('error')
    }
  },
  onConnectComplete: ({ code, state }) => queryClient.getMutationCache().build(queryClient, trpc.integrations.aikido.completeAuth.mutationOptions()).execute({ code, state }),
  getFeatures: () => [],
}
